// ─── What: probability y-axis domain fitting ──────────────────────────────────

export type AxisFit = 'auto' | 'full';

export interface ProbDomain {
    /** Probability units, 0–1. */
    domain: [number, number];
    /** Gap between ticks, probability units. */
    step: number;
    /** Tick positions, probability units. */
    ticks: number[];
    /** Decimal places the labels need at this step. */
    decimals: number;
}

/** The pinned 0–100% axis — every chart's behaviour before autoscaling. */
const FULL: ProbDomain = {
    domain: [0, 1],
    step: 0.25,
    ticks: [0, 0.25, 0.5, 0.75, 1],
    decimals: 0,
};

const MAX_TICKS = 6;
const EPS = 1e-9;

/** Rungs in tenths of a percentage point, so all snapping stays integer maths. */
const RUNGS: Array<{ overSpan: number; stepTenths: number }> = [
    { overSpan: 20, stepTenths: 100 },
    { overSpan: 5, stepTenths: 50 },
    { overSpan: 1, stepTenths: 10 },
    { overSpan: 0.5, stepTenths: 5 },
    { overSpan: -Infinity, stepTenths: 1 },
];

const MAX_TENTHS = 1000;

// ─── How it works ─────────────────────────────────────────────────────────────

/** Fit a y-domain to `values`, snapping bounds to whole 10/5/1/0.5/0.1-point steps. */
export function fitProbabilityDomain(
    values: number[],
    mode: AxisFit
): ProbDomain {
    if (mode === 'full') return FULL;

    // Reduce rather than spread — series can hold hundreds of thousands of points.
    let lo = Infinity;
    let hi = -Infinity;
    for (const v of values) {
        if (!Number.isFinite(v)) continue;
        const pp = v * 100;
        if (pp < lo) lo = pp;
        if (pp > hi) hi = pp;
    }
    if (lo === Infinity) return FULL;
    // Contract is probability units; clamp so stray percent-scale input can't
    // drive the snap past the axis or invert min against max.
    lo = Math.max(0, Math.min(100, lo));
    hi = Math.max(0, Math.min(100, hi));

    // Epsilon on the rung boundaries — 0.55*100 - 0.5*100 is 5.000000000000007.
    const span = hi - lo;
    const step = RUNGS.find(r => span > r.overSpan + EPS)!.stepTenths;

    // Snap out to the next step at each end — no extra padding, or a wide range
    // bleeds to 0-100% (17-83% padded 20% did exactly that). Tenths of a point
    // from here so snapping never drifts on floats.
    // EPS on the rounding too: (0.55*100)*10 is 550.0000000000001, which would
    // otherwise ceil a bound a full step wide and undo the leanness above.
    let min = Math.max(0, Math.floor((lo * 10) / step + EPS) * step);
    let max = Math.min(MAX_TENTHS, Math.ceil((hi * 10) / step - EPS) * step);

    // Widen to at least two steps, raising the top before lowering the bottom.
    while (max - min < 2 * step) {
        if (max < MAX_TENTHS) max = Math.min(MAX_TENTHS, max + step);
        else if (min > 0) min = Math.max(0, min - step);
        else break;
    }

    const all: number[] = [];
    for (let v = min; v <= max; v += step) all.push(v);
    const stride = all.length > MAX_TICKS ? 2 : 1;

    return {
        domain: [min / MAX_TENTHS, max / MAX_TENTHS],
        step: step / MAX_TENTHS,
        ticks: all.filter((_, i) => i % stride === 0).map(v => v / MAX_TENTHS),
        decimals: step < 10 ? 1 : 0,
    };
}

/** Which side of `domain` a value falls outside, or null when it is on scale. */
export function offScaleDirection(
    v: number,
    domain: [number, number]
): 'above' | 'below' | null {
    if (v > domain[1]) return 'above';
    if (v < domain[0]) return 'below';
    return null;
}

/** Clamp a probability into `domain` so edge markers stay visible, not clipped. */
export function clampToDomain(v: number, domain: [number, number]): number {
    return Math.max(domain[0], Math.min(domain[1], v));
}
